import React, { useState } from "react";
import { Box, ButtonBase, IconButton, Tooltip, Typography } from "@mui/material";
import { ArrowUpward, ChevronRight, HeadsetMic, KeyboardArrowDown, MoreHoriz, Shield } from "@mui/icons-material";
import { colors, shadows } from "../../theme/designSystem";

// Molde único dos assistentes (item 228, §6.2a): toda área de IA monta a tela com estas peças, na mesma
// ordem — cabeçalho da área (AreaHeader; FocusBar no celular), conversa sem recuo, pendência em destaque,
// lista de assuntos, nota do rodapé. Área nova troca o conteúdo, nunca a forma.

/**
 * Aviso de retenção e auditoria, igual em todo chat (pessoas, grupos, Ani, Suporte, Agenda, Livro-caixa, Prontuário):
 * faixa no topo da área de conversa. Pedido do Hilton, 28/10/2026.
 */
export const AuditNotice: React.FC<{ clinicName?: string | null }> = ({ clinicName }) => (
  <Box role="note" sx={{ flexShrink: 0, px: 1.5, py: 0.6, bgcolor: "#fdecea", borderBottom: "1px solid #f5c6cb" }}>
    <Typography sx={{ fontSize: "0.7rem", color: "#a12a2a", lineHeight: 1.35 }}>
      As conversas permanecem na lista por <strong>30 dias</strong> e estão sujeitas a auditoria por parte de {clinicName || "sua clínica"}.
    </Typography>
  </Box>
);

/**
 * Coluna de lista dos Assistentes (contatos do chat, histórico da Agenda e do Livro-caixa): uma medida só, para as
 * três colunas terem a mesma largura e o mesmo respiro (padrão do Hilton, 28/10/2026).
 */
export const LIST_COLUMN_WIDTH = 320;
export const listColumnSx = {
  width: LIST_COLUMN_WIDTH, flexShrink: 0, display: "flex", flexDirection: "column", minHeight: 0,
  borderRight: `1px solid ${colors.backgroundAlt}`, bgcolor: colors.white,
} as const;
/** Topo da coluna: busca e filtros. */
export const listHeaderSx = { flexShrink: 0, p: 1.5, pb: 1, display: "flex", flexDirection: "column", gap: 1.25 } as const;
export const listSearchSx = { display: "flex", alignItems: "center", gap: 1, px: 1.5, minHeight: 40, borderRadius: "22px", bgcolor: colors.background } as const;
export const listChipSx = (selected: boolean) => ({
  px: 1.25, minHeight: 28, borderRadius: "14px", fontSize: "0.75rem", fontWeight: 600, fontFamily: "inherit",
  color: selected ? colors.white : colors.textSecondary, bgcolor: selected ? colors.primary : colors.background,
  "&:hover": { bgcolor: selected ? colors.primaryHover : colors.backgroundAlt },
  "&.Mui-focusVisible": { outline: `2px solid ${colors.primary}`, outlineOffset: 2 },
});
/** Linha da lista: largura toda, ativa com fundo claro e barra à esquerda. */
export const listRowSx = (active: boolean) => ({
  width: "100%", display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1.1, textAlign: "left", justifyContent: "flex-start",
  bgcolor: active ? colors.primaryLight : "transparent",
  borderLeft: `3px solid ${active ? colors.primary : "transparent"}`,
  "&:hover": { bgcolor: active ? colors.primaryLight : colors.background },
});

/**
 * Largura da conversa: texto corrido (blocos `c4-text`) para em 880px, porque linha muito longa cansa a leitura;
 * tabela, cartão e caixa de texto usam a largura toda da área.
 */
export const TEXT_MAX_WIDTH = 880;
export const conversationSx = { "& .c4-text": { maxWidth: TEXT_MAX_WIDTH } } as const;

/**
 * Cabeçalho de área no computador: círculo de 48px (ícone ou foto), título, linha de apoio e, à direita,
 * selo de garantia e ações. Agenda, Livro-caixa, Prontuário e Conversas usam este, e nenhum outro.
 */
export const AreaHeader: React.FC<{
  avatar: React.ReactNode;
  title: React.ReactNode;
  /** Ao lado do título, em texto menor (ex.: ID do paciente) */
  titleExtra?: React.ReactNode;
  meta?: React.ReactNode;
  /** Antes do círculo (ex.: voltar) */
  leading?: React.ReactNode;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ avatar, title, titleExtra, meta, leading, badge, actions }) => (
  <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1.5, px: { xs: 1.5, md: 4 }, py: 1.5, borderBottom: `1px solid ${colors.backgroundAlt}`, bgcolor: colors.white }}>
    {leading}
    <Box sx={{ width: 48, height: 48, borderRadius: "50%", flexShrink: 0, overflow: "hidden", bgcolor: colors.primarySoft, color: colors.primaryText,
      display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: "0.95rem" }}>
      {avatar}
    </Box>
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, minWidth: 0 }}>
        <Typography component="h2" sx={{ fontSize: "1rem", fontWeight: 600, color: colors.textStrong, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {title}
        </Typography>
        {titleExtra && <Typography component="span" sx={{ fontSize: "0.75rem", color: colors.textSecondary, flexShrink: 0 }}>{titleExtra}</Typography>}
      </Box>
      {meta && (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "2px 6px", fontSize: "0.8125rem", color: colors.textSecondary, minWidth: 0 }}>
          {meta}
        </Box>
      )}
    </Box>
    {badge}
    {actions}
  </Box>
);

/** Link de texto da linha de apoio do cabeçalho (ex.: "Abrir a Agenda", "Ver cadastro"). */
export const HeaderLink: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <Box component="button" type="button" onClick={onClick}
    sx={{ border: "none", background: "none", p: 0, color: colors.primaryText, fontWeight: 500, fontSize: "0.8125rem", cursor: "pointer", fontFamily: "inherit",
      textDecoration: "underline", textUnderlineOffset: "3px", textDecorationColor: colors.primarySoft, "&:hover": { textDecorationColor: colors.primaryText } }}>
    {children}
  </Box>
);

/**
 * Selo de garantia à direita do cabeçalho (o que a área nunca faz sozinha). Encolhe com reticências em tela
 * estreita; o texto inteiro (ou a explicação do `tooltip`) fica no balão.
 */
export const HeaderBadge: React.FC<{ children: string; tooltip?: string }> = ({ children, tooltip }) => (
  <Tooltip title={tooltip ?? children} describeChild>
    <Box component="button" type="button"
      sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: "0.75rem", fontFamily: "inherit", cursor: "help", color: colors.textPrimary,
        bgcolor: colors.primaryLight, border: `1px solid ${colors.primarySoft}`, px: 1.25, py: 0.75, borderRadius: "8px", flexShrink: 1, minWidth: 0, maxWidth: "40%" }}>
      <Shield sx={{ fontSize: 16, color: colors.primaryText, flexShrink: 0 }} aria-hidden />
      <Box component="span" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{children}</Box>
    </Box>
  </Tooltip>
);

/** Quem fala: círculo de 24px + nome numa linha; o conteúdo vem embaixo, na largura toda. */
export const SpeakerLabel: React.FC<{ isUser: boolean; initial: string; name: string }> = ({ isUser, initial, name }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
    <Box aria-hidden sx={{ width: 24, height: 24, flexShrink: 0, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.625rem", fontWeight: 700,
      bgcolor: isUser ? colors.primaryText : colors.primaryLight, color: isUser ? colors.white : colors.primaryText }}>
      {isUser ? initial : "IA"}
    </Box>
    <Typography component="span" sx={{ fontSize: "0.8125rem", fontWeight: 600, color: colors.textSecondary }}>{name}</Typography>
  </Box>
);

/** Etiqueta ao lado do nome do agente de alteração da área: separa quem consulta de quem prepara alterações. */
export const WriterTag: React.FC = () => (
  <Box component="span" sx={{ mb: 0.75, fontSize: "0.6875rem", fontWeight: 600, color: colors.warningSoftText, bgcolor: colors.warningSoft, px: 1, py: 0.25, borderRadius: "10px" }}>
    prepara alterações
  </Box>
);

/** Barra do foco: o assunto fixo da sessão (paciente, dia da agenda, conta) numa linha, com até duas ações de ícone. */
export const FocusBar: React.FC<{
  avatar: React.ReactNode;
  title: string;
  meta: React.ReactNode;
  /** Com seletor (sessões abertas), o título vira botão com seta */
  onTitleClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  titleAriaLabel?: string;
  actions?: React.ReactNode;
}> = ({ avatar, title, meta, onTitleClick, titleAriaLabel, actions }) => {
  const text = (
    <>
      <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <Typography component="h2" sx={{ fontSize: "0.9375rem", fontWeight: 600, color: colors.textStrong, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</Typography>
        {onTitleClick && <KeyboardArrowDown aria-hidden sx={{ fontSize: 20, color: colors.textSecondary, flexShrink: 0 }} />}
      </Box>
      <Box component="span" sx={{ fontSize: "0.8125rem", color: colors.textSecondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>{meta}</Box>
    </>
  );
  return (
    <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1.25, minHeight: 56, py: 0.5, pl: 1.5, pr: 0.5, borderBottom: `1px solid ${colors.backgroundAlt}`, bgcolor: colors.white }}>
      <Box sx={{ width: 40, height: 40, borderRadius: "50%", flexShrink: 0, overflow: "hidden", bgcolor: colors.primarySoft, color: colors.primaryText,
        display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: "0.8125rem" }}>
        {avatar}
      </Box>
      {onTitleClick ? (
        <ButtonBase onClick={onTitleClick} aria-label={titleAriaLabel} aria-haspopup="menu"
          sx={{ flex: 1, minWidth: 0, minHeight: 44, justifyContent: "flex-start", textAlign: "left", flexDirection: "column", alignItems: "flex-start", borderRadius: "8px",
            "&.Mui-focusVisible": { outline: `2px solid ${colors.primary}`, outlineOffset: 2 } }}>
          {text}
        </ButtonBase>
      ) : (
        <Box sx={{ flex: 1, minWidth: 0 }}>{text}</Box>
      )}
      {actions}
    </Box>
  );
};

/** Pendência em destaque: a primeira coisa da abertura quando há algo esperando a pessoa. */
export const PendingAction: React.FC<{ icon: React.ReactNode; title: string; detail?: string; onClick: () => void; disabled?: boolean }> = ({ icon, title, detail, onClick, disabled }) => (
  <ButtonBase onClick={onClick} disabled={disabled}
    sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1.5, minHeight: 56, px: 1.5, py: 1, justifyContent: "flex-start", textAlign: "left",
      border: "1px solid #ffe8a3", borderRadius: "8px", bgcolor: "#fff8e1", "&:hover": { bgcolor: colors.warningSoft },
      "&.Mui-focusVisible": { outline: `2px solid ${colors.primary}`, outlineOffset: 2 } }}>
    <Box aria-hidden sx={{ width: 36, height: 36, borderRadius: "8px", flexShrink: 0, bgcolor: colors.warningSoft, color: colors.warningSoftText,
      display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 20 } }}>{icon}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: colors.textStrong }}>{title}</Typography>
      {detail && <Typography sx={{ fontSize: "0.8125rem", color: colors.warningSoftText }}>{detail}</Typography>}
    </Box>
    <ChevronRight aria-hidden sx={{ color: colors.warningSoftText, fontSize: 20 }} />
  </ButtonBase>
);

export interface AssistantTopic {
  title: string;
  does: string;
  icon: React.ReactElement;
  onClick: () => void;
}

/**
 * Assuntos da área: no celular uma lista única (uma linha por assunto, descrição sem quebrar); no desktop
 * uma grade de 2 colunas. Mostra os primeiros e guarda o resto em "Mais assuntos".
 */
export const TopicList: React.FC<{ topics: AssistantTopic[]; isMobile: boolean; disabled?: boolean; visible?: number }> = ({ topics, isMobile, disabled, visible = 4 }) => {
  const [expanded, setExpanded] = useState(false);
  const overflow = topics.length > visible + 1 && !expanded;
  const shown = overflow ? topics.slice(0, visible) : topics;
  const hidden = overflow ? topics.slice(visible) : [];

  const row = (key: string, icon: React.ReactNode, title: string, does: string, onClick: () => void, muted = false, last = false) => (
    <ButtonBase key={key} role="listitem" onClick={onClick} disabled={disabled}
      sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1.5, minHeight: 52, px: 1.5, py: 0.75, justifyContent: "flex-start", textAlign: "left", bgcolor: colors.white,
        ...(isMobile
          ? { borderBottom: last ? "none" : `1px solid ${colors.backgroundSubtle}` }
          : { border: `1px solid ${colors.backgroundAlt}`, borderRadius: "8px" }),
        "&:hover": { bgcolor: colors.primaryLight },
        "&.Mui-focusVisible": { outline: `2px solid ${colors.primary}`, outlineOffset: -2 } }}>
      <Box aria-hidden sx={{ width: 32, height: 32, borderRadius: "8px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: muted ? colors.background : colors.primaryLight, color: muted ? colors.textPrimary : colors.primaryText, "& svg": { fontSize: 18 } }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: isMobile ? "0.9375rem" : "0.875rem", fontWeight: 500, color: colors.textStrong }}>{title}</Typography>
        <Typography sx={{ fontSize: isMobile ? "0.8125rem" : "0.75rem", color: colors.textSecondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{does}</Typography>
      </Box>
      {isMobile && <ChevronRight aria-hidden sx={{ color: colors.borderHover, fontSize: 18 }} />}
    </ButtonBase>
  );

  const items = [
    ...shown.map((t, i) => row(t.title, t.icon, t.title, t.does, t.onClick, false, !overflow && i === shown.length - 1)),
    ...(overflow
      ? [row("more", <MoreHoriz />, "Mais assuntos", hidden.map((t) => t.title.toLowerCase()).join(", "), () => setExpanded(true), true, true)]
      : []),
  ];

  return (
    <Box role="list" aria-label="Assuntos" className="c4-text"
      sx={isMobile
        ? { border: `1px solid ${colors.backgroundAlt}`, borderRadius: "8px", overflow: "hidden" }
        : { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
      {items}
    </Box>
  );
};

/** Nota do rodapé: uma linha, a regra que mais importa na área. */
export const AssistantNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary, textAlign: "center", mt: 0.75 }}>{children}</Typography>
);

/**
 * Sugestões de ação da IA ("próximos passos"): um só desenho em todo o super assistente — Agenda, Prontuário,
 * Livro-caixa e Conversas (linguagem visual única, 28/10). No celular viram lista, um toque cada.
 */
export const ActionChips: React.FC<{ actions: string[]; onPick: (action: string) => void; isMobile: boolean; disabled?: boolean }> = ({ actions, onPick, isMobile, disabled }) =>
  actions.length === 0 ? null : (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.25, flexDirection: isMobile ? "column" : "row" }}>
      {actions.map((a) => (
        <ButtonBase key={a} onClick={() => onPick(a)} disabled={disabled}
          sx={{ fontFamily: "inherit", textAlign: "left", justifyContent: "flex-start", fontSize: "0.8125rem", fontWeight: 500, color: colors.primaryText,
            bgcolor: colors.primaryLight, px: 1.5, py: 0.75, borderRadius: "8px", minHeight: isMobile ? 44 : 32,
            "&:hover": { bgcolor: colors.primarySoft }, "&.Mui-disabled": { opacity: 0.6 },
            "&.Mui-focusVisible": { outline: `2px solid ${colors.primary}`, outlineOffset: 2 } }}>
          {a}
        </ButtonBase>
      ))}
    </Box>
  );

/**
 * Botões da caixa de mensagem, sempre na mesma ordem em toda área: anexar · [texto] · fone · microfone · enviar.
 * Cada área liga só os que usa; o desenho e o lugar são estes.
 */
export const ComposerIconButton: React.FC<{
  label: string;
  title?: string;
  onClick: () => void;
  isMobile: boolean;
  pressed?: boolean;
  tone?: "plain" | "voice" | "recording";
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ label, title, onClick, isMobile, pressed, tone = "plain", disabled, children }) => {
  const active = pressed && tone !== "plain";
  return (
    <Tooltip title={title ?? label}>
      <span>
        <IconButton onClick={onClick} aria-label={label} aria-pressed={pressed} disabled={disabled}
          sx={{ width: isMobile ? 44 : 40, height: isMobile ? 44 : 40, borderRadius: isMobile ? "50%" : "8px",
            color: active ? (tone === "recording" ? colors.errorHover : colors.primaryText) : tone === "plain" ? colors.textPrimary : colors.textSecondary,
            bgcolor: active ? (tone === "recording" ? colors.errorSoft : colors.primaryLight) : "transparent" }}>
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export const SendButton: React.FC<{ onClick: () => void; disabled: boolean; isMobile: boolean }> = ({ onClick, disabled, isMobile }) => (
  <IconButton onClick={onClick} disabled={disabled} aria-label="Enviar"
    sx={{ width: isMobile ? 44 : 40, height: isMobile ? 44 : 40, borderRadius: isMobile ? "50%" : "8px", bgcolor: colors.primary, color: colors.white, flexShrink: 0,
      "&:hover": { bgcolor: colors.primaryHover }, "&.Mui-disabled": { bgcolor: colors.borderHover, color: colors.white } }}>
    <ArrowUpward sx={{ fontSize: 20 }} />
  </IconButton>
);

/** Moldura da caixa de mensagem: borda, foco e cantos iguais em toda área; o conteúdo (campo e botões) vem de fora. */
export const ComposerFrame: React.FC<{ isMobile: boolean; disabled?: boolean; children: React.ReactNode }> = ({ isMobile, disabled, children }) => (
  <Box sx={{ display: "flex", alignItems: isMobile ? "center" : "flex-end", gap: 1, border: `1px solid ${colors.border}`, borderRadius: isMobile ? "24px" : "8px",
    p: isMobile ? "4px" : "8px", bgcolor: disabled ? colors.disabledBackground : colors.white,
    "&:focus-within": { borderColor: colors.primary, boxShadow: shadows.focus } }}>
    {children}
  </Box>
);

/** Linha de estado do modo conversa (RN-570): o que o assistente está fazendo agora. */
export const VoiceStatus: React.FC<{ speaking: boolean; sending: boolean; transcribing: boolean; listening: boolean }> = ({ speaking, sending, transcribing, listening }) => (
  <Box role="status" aria-live="polite" sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1, fontSize: "0.8125rem", color: colors.primaryText }}>
    <HeadsetMic sx={{ fontSize: 16 }} aria-hidden />
    {speaking
      ? "Falando… toque no microfone para interromper."
      : sending
        ? "Consultando…"
        : transcribing
          ? "Transcrevendo…"
          : listening
            ? "Ouvindo. Faça uma pausa para enviar."
            : "Modo conversa pausado. Toque no microfone para falar."}
  </Box>
);
